import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './ScheduleView.css';

function ScheduleView() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [savedSchedules, setSavedSchedules] = useState([]);
  const [schedulesByBranch, setSchedulesByBranch] = useState({}); // {branchId: [schedules]}
  const [branches, setBranches] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [loadingScheduleData, setLoadingScheduleData] = useState(false);
  const [schoolTimings, setSchoolTimings] = useState(null);
  const [schedulesBySemester, setSchedulesBySemester] = useState({}); // {semester: scheduleData}
  // Filters/Search/Sort/Pagination (yellow-marked requirement)
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClassId, setFilterClassId] = useState('all');
  const [filterSemester, setFilterSemester] = useState('all'); // all | s1 | s2
  const [filterAcademicYear, setFilterAcademicYear] = useState('all');
  const [academicYears, setAcademicYears] = useState([]);
  const [sortKey, setSortKey] = useState('class'); // class | section | created | updated
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    console.log('=== SCHOOL DATA LOADING ===');
    console.log('Stored school data from localStorage:', storedSchoolData);
    
    if (storedSchoolData) {
      const parsedData = JSON.parse(storedSchoolData);
      console.log('Parsed school data:', parsedData);
      setSchoolData(parsedData);
    } else {
      console.log('No school data found in localStorage');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchBranches();
      fetchSavedSchedules();
      fetchSchoolTimings();
      fetchAcademicYears();
    }
  }, [schoolData?.id, selectedBranchId]);

  const fetchSchoolTimings = async () => {
    try {
      console.log('Fetching school timings for school:', schoolData.id);
      
      let query = supabase
        .from('school_timings')
        .select('*')
        .eq('school_id', schoolData.id)
        .is('class_id', null); // School-wide timings only

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching school timings:', error);
        return;
      }
      
      if (data && data.length > 0) {
        setSchoolTimings(data[0]);
        console.log('Found school timings:', data[0]);
        console.log('School timings details:', {
          start_time: data[0].start_time,
          period_time: data[0].period_time,
          end_time: data[0].end_time
        });
      } else {
        console.log('No school timings found');
        setSchoolTimings(null);
      }
    } catch (err) {
      console.error('Error fetching school timings:', err);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('start_date', { ascending: false });
      if (!error) setAcademicYears(data || []);
    } catch (err) {
      // fail silently; filters remain usable without years
    }
  };

  // Fetch branches
  const fetchBranches = async () => {
    if (!schoolData?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('branch_name');
      
      if (error) {
        console.error('Error fetching branches:', error);
        return;
      }
      
      setBranches(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchSavedSchedules = async () => {
    try {
      setLoadingSchedules(true);
      console.log('=== DEBUGGING SCHEDULE FETCH ===');
      console.log('School data:', schoolData);
      console.log('School ID:', schoolData?.id);
      console.log('Fetching schedules from Supabase for school:', schoolData.id);
      
      // Get all saved schedules from Supabase
      let query = supabase
        .from('class_schedules')
        .select('*')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        // When a specific branch is selected, show only that branch's schedules
        query = query.eq('branch_id', selectedBranchId);
        
        const { data, error } = await query.order('created_at', { ascending: false });

      console.log('Supabase query result:', { data, error });
      
      // DEBUG: Let's also check ALL schedules in the database to see what's there
      const { data: allSchedulesDebug, error: allSchedulesError } = await supabase
        .from('class_schedules')
        .select('*');
      
      console.log('=== ALL SCHEDULES IN DATABASE ===');
      console.log('All schedules:', allSchedulesDebug);
      console.log('All schedules error:', allSchedulesError);
      console.log('Total schedules in database:', allSchedulesDebug?.length || 0);
      
      if (allSchedulesDebug && allSchedulesDebug.length > 0) {
        console.log('Sample schedule entry:', allSchedulesDebug[0]);
        console.log('School IDs in database:', [...new Set(allSchedulesDebug.map(s => s.school_id))]);
      }
      
      if (error) {
        console.error('Error fetching schedules from Supabase:', error);
        setSavedSchedules([]);
        return;
      }

      console.log('Raw schedule data from Supabase:', data);
      console.log('Number of schedule entries found:', data?.length || 0);

      // Get unique class IDs from the schedule data
      const uniqueClassIds = [...new Set(data.map(entry => entry.class_id))];
      console.log('Unique class IDs:', uniqueClassIds);

      // Fetch class names for these class IDs
      let classNames = {};
      if (uniqueClassIds.length > 0) {
        const { data: classesData, error: classesError } = await supabase
          .from('classes')
          .select('id, class_name')
          .in('id', uniqueClassIds);

        if (classesError) {
          console.error('Error fetching class names:', classesError);
        } else {
          classNames = classesData.reduce((acc, cls) => {
            acc[cls.id] = cls.class_name;
            return acc;
          }, {});
          console.log('Class names mapping:', classNames);
        }
      }

      // Group by class_id and section_name to get unique schedules
      const scheduleMap = new Map();
      
      data.forEach(entry => {
        const key = `${entry.class_id}_${entry.section_name}`;
        if (!scheduleMap.has(key)) {
          scheduleMap.set(key, {
            key: key,
            class_id: entry.class_id,
            class_name: classNames[entry.class_id] || `Class ${entry.class_id}`,
            section_name: entry.section_name,
            created_at: entry.created_at,
            updated_at: entry.updated_at
          });
        }
      });

      const allSchedules = Array.from(scheduleMap.values());
      
      console.log('=== FINAL SCHEDULE PROCESSING ===');
      console.log('Schedule map size:', scheduleMap.size);
      console.log('All schedules array:', allSchedules);
      console.log('All schedules length:', allSchedules.length);
      
      // Sort by class name and section
      allSchedules.sort((a, b) => {
        if (a.class_name !== b.class_name) {
          return a.class_name.localeCompare(b.class_name);
        }
        return a.section_name.localeCompare(b.section_name);
      });
      
      console.log('Final processed schedules:', allSchedules);
      console.log('Setting savedSchedules to:', allSchedules);
      setSavedSchedules(allSchedules);
      setSchedulesByBranch({}); // Clear branch-wise schedules
      
      } else {
        // When "All Branches" is selected, fetch all schedules and group by branch
        const { data, error } = await query.order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching schedules from Supabase:', error);
          setSavedSchedules([]);
          setSchedulesByBranch({});
          return;
        }
        
        // Get unique class IDs
        const uniqueClassIds = [...new Set(data.map(entry => entry.class_id))];
        
        // Fetch class names
        let classNames = {};
        if (uniqueClassIds.length > 0) {
          const { data: classesData, error: classesError } = await supabase
            .from('classes')
            .select('id, class_name')
            .in('id', uniqueClassIds);
          
          if (!classesError) {
            classNames = classesData.reduce((acc, cls) => {
              acc[cls.id] = cls.class_name;
              return acc;
            }, {});
          }
        }
        
        // Group by branch_id
        const branchGroups = {};
        
        data.forEach(entry => {
          const branchId = entry.branch_id || 'null';
          if (!branchGroups[branchId]) {
            branchGroups[branchId] = [];
          }
          
          const key = `${entry.class_id}_${entry.section_name}`;
          if (!branchGroups[branchId].find(s => s.key === key)) {
            branchGroups[branchId].push({
              key: key,
              class_id: entry.class_id,
              class_name: classNames[entry.class_id] || `Class ${entry.class_id}`,
              section_name: entry.section_name,
              branch_id: entry.branch_id,
              created_at: entry.created_at,
              updated_at: entry.updated_at
            });
          }
        });
        
        setSchedulesByBranch(branchGroups);
        setSavedSchedules([]); // Clear single branch schedules
      }
    } catch (err) {
      console.error('Error fetching saved schedules:', err);
      setSavedSchedules([]);
      setSchedulesByBranch({});
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleScheduleSelect = async (schedule) => {
    setSelectedSchedule(schedule);
    setLoadingScheduleData(true);
    setSchedulesBySemester({});
    
    try {
      console.log('Loading schedule data from Supabase for:', schedule);
      
      // Load ALL schedule data from Supabase for this class and section (all semesters)
      let query = supabase
        .from('class_schedules')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', schedule.class_id)
        .eq('section_name', schedule.section_name);

      // Use schedule's branch_id if it exists, otherwise use selectedBranchId
      // If schedule has branch_id (from "All Branches" view), use it
      // Otherwise, use selectedBranchId or null filter
      const branchIdToUse = schedule.branch_id !== undefined ? schedule.branch_id : selectedBranchId;
      
      if (branchIdToUse) {
        query = query.eq('branch_id', branchIdToUse);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query
        .order('semester')
        .order('day')
        .order('period');

      if (error) {
        console.error('Error loading schedule data:', error);
        alert('Error loading schedule data');
        return;
      }

      if (data && data.length > 0) {
        // Group schedules by semester
        const schedulesGrouped = {};
        
        data.forEach(entry => {
          const semester = entry.semester || '1st Semester';
          
          if (!schedulesGrouped[semester]) {
            schedulesGrouped[semester] = {
              semester: semester,
              semester_name: entry.semester_name || semester,
              schedule: {},
              created_at: entry.created_at,
              updated_at: entry.updated_at
            };
          }
          
          const key = `${entry.day}_${entry.period}`;
          schedulesGrouped[semester].schedule[key] = {
            subject_name: entry.subject_name,
            teacher_name: entry.teacher_name,
            teacher_id: entry.teacher_id,
            subject_id: entry.subject_id
          };
        });

        // Sort semesters: 1st Semester first, then others
        const sortedSemesters = Object.keys(schedulesGrouped).sort((a, b) => {
          if (a === '1st Semester') return -1;
          if (b === '1st Semester') return 1;
          return a.localeCompare(b);
        });

        // Create ordered object
        const orderedSchedules = {};
        sortedSemesters.forEach(sem => {
          orderedSchedules[sem] = schedulesGrouped[sem];
        });

        setSchedulesBySemester(orderedSchedules);
        
        // Set the first semester as default scheduleData for backward compatibility
        const firstSemester = sortedSemesters[0];
        if (firstSemester) {
          const firstScheduleData = {
            school_id: schoolData.id,
            class_id: schedule.class_id,
            class_name: schedule.class_name,
            section_name: schedule.section_name,
            schedule: orderedSchedules[firstSemester].schedule,
            created_at: orderedSchedules[firstSemester].created_at,
            updated_at: orderedSchedules[firstSemester].updated_at,
            semester: firstSemester,
            semester_name: orderedSchedules[firstSemester].semester_name
          };
          setScheduleData(firstScheduleData);
        }

        console.log('✅ Loaded schedule data from Supabase:', orderedSchedules);
      } else {
        console.log('No schedule data found');
        setScheduleData(null);
        setSchedulesBySemester({});
      }
    } catch (err) {
      console.error('Error loading schedule data:', err);
      alert('Error loading schedule data');
    } finally {
      setLoadingScheduleData(false);
    }
  };

  useEffect(() => {
    setSelectedSchedule(null);
    setScheduleData(null);
    setSchedulesBySemester({});
  }, [selectedBranchId]);

  const getWorkingDays = () => {
    if (!scheduleData || !scheduleData.schedule) {
      return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    }
    
    // Extract unique days from the schedule data
    const days = new Set();
    Object.keys(scheduleData.schedule).forEach(key => {
      const [day] = key.split('_');
      days.add(day);
    });
    
    // Define the order of days
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    // Return days in order, filtered to only include days that exist in the schedule
    return dayOrder.filter(day => days.has(day));
  };

  const getPeriods = () => {
    if (!scheduleData || !scheduleData.schedule) return [];
    
    const periods = new Set();
    Object.keys(scheduleData.schedule).forEach(key => {
      const [day, period] = key.split('_');
      periods.add(parseInt(period));
    });
    
    return Array.from(periods).sort((a, b) => a - b);
  };

  const getDayName = (day) => {
    const dayNames = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    };
    return dayNames[day] || day;
  };

  // Helper functions for period timings
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    
    // Convert to string and clean up
    let time = timeString.toString().trim();
    
    // Handle various time formats and ensure HH:MM format
    if (time.match(/^\d{1,2}$/)) {
      // Single or double digit hour (e.g., "8" or "08")
      time = time.padStart(2, '0') + ':00';
    } else if (time.match(/^\d{1,2}:\d{1,2}$/)) {
      // HH:M or H:MM format (e.g., "8:30" or "08:3")
      const parts = time.split(':');
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      time = `${hours}:${minutes}`;
    } else if (time.match(/^\d{1,2}:\d{2}:\d{2}$/)) {
      // HH:MM:SS format - remove seconds
      time = time.substring(0, 5);
    } else if (time.match(/^\d{4}$/)) {
      // HHMM format (e.g., "0830")
      time = time.substring(0, 2) + ':' + time.substring(2, 4);
    }
    
    return time;
  };

  const addMinutes = (timeString, minutes) => {
    if (!timeString) return 'N/A';
    
    // Ensure time is in HH:MM format
    const time = formatTime(timeString);
    const [hours, mins] = time.split(':').map(Number);
    
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60);
    const newMins = totalMinutes % 60;
    
    return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
  };

  const calculatePeriodTimings = (periodNumber) => {
    console.log('Calculating timings for period:', periodNumber);
    console.log('School timings available:', schoolTimings);
    
    if (!schoolTimings) {
      console.log('No school timings available');
      return { start: 'N/A', end: 'N/A', breakAfter: null };
    }
    
    const startTime = formatTime(schoolTimings.start_time);
    const periodDuration = schoolTimings.period_time || 40;
    const breaks = schoolTimings.breaks || [];
    
    console.log('Start time:', startTime, 'Period duration:', periodDuration);
    
    // Calculate period start time: periods are consecutive
    // Period 1: startTime
    // Period 2: startTime + periodDuration
    // Period 3: startTime + 2 * periodDuration
    // etc.
    
    // Calculate cumulative time for periods before this one
    let periodStartTime = startTime;
    
    for (let i = 1; i < periodNumber; i++) {
      // Calculate when period i ends
      const periodIEnd = addMinutes(periodStartTime, periodDuration);
      
      // Check if there's a break after period i
      const breakAfter = breaks.find(breakItem => {
        const breakStart = formatTime(breakItem.startTime);
        return breakStart === periodIEnd;
      });
      
      if (breakAfter) {
        // If there's a break, next period starts after break ends
        periodStartTime = formatTime(breakAfter.endTime);
      } else {
        // No break, so next period starts immediately after this period ends
        periodStartTime = periodIEnd;
      }
    }
    
    const periodEndTime = addMinutes(periodStartTime, periodDuration);
    
    // Check if there's a break after this period
    const breakAfter = breaks.find(breakItem => {
      const breakStart = formatTime(breakItem.startTime);
      return breakStart === periodEndTime;
    });
    
    const result = {
      start: periodStartTime,
      end: periodEndTime,
      breakAfter: breakAfter
    };
    
    console.log(`Period ${periodNumber} result:`, result);
    return result;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return 'Invalid Date';
    }
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content nexy-schedule-view-page">
        {/* Page Header */}
        <div className="nexy-schedule-view-header">
          <div className="nexy-header-left">
            <div className="nexy-header-icon">📊</div>
            <h1>Schedule View</h1>
          </div>
          <div className="nexy-header-right">
            <p>View and manage class schedules</p>
          </div>
        </div>
        <div className="dashboard-branch-row schedule-view-branch-row">
          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />
          <div className="branch-context">
            {selectedBranchId ? 'Filtering schedules to the selected branch' : 'Showing schedules for all branches'}
          </div>
        </div>

        {/* Search and Filters Section */}
        <div className="nexy-filters-section">
          <div className="nexy-search-wrapper">
            <span className="nexy-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search class/section..."
              value={searchTerm}
              onChange={(e)=>{setSearchTerm(e.target.value); setPage(1);}}
              className="nexy-search-input"
            />
          </div>
          <div className="nexy-filters-container">
            <select 
              value={filterClassId} 
              onChange={(e)=>{setFilterClassId(e.target.value); setPage(1);}}
              className="nexy-filter-dropdown"
            >
              <option value="all">All Classes</option>
              {[...new Set(savedSchedules.map(s=>({id:s.class_id,name:s.class_name})))].map((c,i)=> (
                <option key={i} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select 
              value={filterAcademicYear} 
              onChange={(e)=>{setFilterAcademicYear(e.target.value); setPage(1);}}
              className="nexy-filter-dropdown"
            >
              <option value="all">All Academic Years</option>
              {academicYears.map(y => (
                <option key={y.id} value={y.id}>{new Date(y.start_date).getFullYear()} - {new Date(y.end_date).getFullYear()}</option>
              ))}
            </select>
            <select 
              value={filterSemester} 
              onChange={(e)=>{setFilterSemester(e.target.value); setPage(1);}}
              className="nexy-filter-dropdown"
            >
              <option value="all">All Semesters</option>
              <option value="s1">Semester 1</option>
              <option value="s2">Semester 2</option>
            </select>
            <select 
              value={sortKey} 
              onChange={(e)=>setSortKey(e.target.value)}
              className="nexy-filter-dropdown"
            >
              <option value="class">Sort: Class</option>
              <option value="section">Sort: Section</option>
              <option value="created">Sort: Created</option>
              <option value="updated">Sort: Updated</option>
            </select>
            <select 
              value={sortDir} 
              onChange={(e)=>setSortDir(e.target.value)}
              className="nexy-filter-dropdown"
            >
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </div>
        </div>

        <div className="schedule-view-container">
          {/* Saved Schedules List */}
          <div className="schedules-list-section">
            <div className="nexy-saved-schedules-header">
              <div className="nexy-section-title-wrapper">
                <div className="nexy-clipboard-icon">📋</div>
                <h2>Saved Schedules</h2>
              </div>
            </div>

            {loadingSchedules ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading schedules...</p>
              </div>
            ) : (!selectedBranchId && Object.keys(schedulesByBranch).length === 0 && savedSchedules.length === 0) ? (
              <div className="nexy-empty-state">
                <div className="nexy-empty-icon">📅</div>
                <h3>No Schedules Found</h3>
                <p>No class schedules have been created yet.</p>
                <button 
                  onClick={() => navigate('/school/class-schedule')}
                  className="nexy-create-schedule-btn"
                >
                  📋 Create Schedule
                </button>
              </div>
            ) : (!selectedBranchId && Object.keys(schedulesByBranch).length > 0) ? (
              // Branch-wise schedules display when "All Branches" is selected
              <div className="branch-wise-schedules-section">
                <h2 style={{ 
                  color: '#1F2937', 
                  fontSize: '1.5rem', 
                  fontWeight: '700', 
                  marginBottom: '1.5rem',
                  borderBottom: '2px solid #E5E7EB',
                  paddingBottom: '0.75rem'
                }}>
                  📍 Branch-wise Class Schedules
                </h2>
                
                {Object.keys(schedulesByBranch).map(branchId => {
                  const branch = branchId === 'null' ? null : branches.find(b => b.id === parseInt(branchId));
                  const schedules = schedulesByBranch[branchId] || [];
                  
                  if (schedules.length === 0) return null;
                  
                  return (
                    <div key={branchId} style={{ marginBottom: '2.5rem' }}>
                      <h3 style={{
                        color: '#FF8B3D',
                        fontSize: '1.25rem',
                        fontWeight: '700',
                        marginBottom: '1rem',
                        paddingBottom: '0.75rem',
                        borderBottom: '2px solid #FF8B3D'
                      }}>
                        {branch ? branch.branch_name : 'No Branch Assigned'}
                      </h3>
                      
                      <div className="nexy-schedules-grid">
                        {schedules
                          .filter(s =>
                            (filterClassId === 'all' || s.class_id === parseInt(filterClassId)) &&
                            (`${s.class_name} ${s.section_name}`.toLowerCase().includes(searchTerm.toLowerCase()))
                          )
                          .sort((a, b) => {
                            if (a.class_name !== b.class_name) {
                              return a.class_name.localeCompare(b.class_name);
                            }
                            return a.section_name.localeCompare(b.section_name);
                          })
                          .map((schedule, index) => (
                            <div 
                              key={`${schedule.key}_${index}`} 
                              className={`nexy-schedule-card ${selectedSchedule?.key === schedule.key ? 'nexy-selected' : ''}`}
                              onClick={() => handleScheduleSelect(schedule)}
                            >
                              <div className="nexy-card-header">
                                <h3 className="nexy-grade-heading">{schedule.class_name}</h3>
                                <span className="nexy-section-badge">{schedule.section_name}</span>
                              </div>
                              <div className="nexy-timestamps-section">
                                <div className="nexy-timestamp-row">
                                  <span className="nexy-timestamp-label">Created:</span>
                                  <span className="nexy-timestamp-value">{formatDate(schedule.created_at)}</span>
                                </div>
                                <div className="nexy-timestamp-row">
                                  <span className="nexy-timestamp-label">Updated:</span>
                                  <span className="nexy-timestamp-value">{formatDate(schedule.updated_at)}</span>
                                </div>
                              </div>
                              <button 
                                className="nexy-view-schedule-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleScheduleSelect(schedule);
                                }}
                              >
                                <span className="nexy-view-icon">👁️</span>
                                View Schedule
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="nexy-schedules-grid">
                {(() => {
                  // filter helpers
                  const withinAcademicYear = (item) => {
                    if (filterAcademicYear === 'all') return true;
                    const year = academicYears.find(y => y.id === filterAcademicYear);
                    if (!year) return true;
                    const created = new Date(item.created_at);
                    return created >= new Date(year.start_date) && created <= new Date(year.end_date);
                  };
                  const withinSemester = (item) => {
                    if (filterSemester === 'all') return true;
                    const year = academicYears.find(y => y.id === filterAcademicYear);
                    if (!year) return true; // need year to split semesters
                    const mid = new Date(year.start_date);
                    const end = new Date(year.end_date);
                    const half = new Date((mid.getTime()+end.getTime())/2);
                    const created = new Date(item.created_at);
                    return filterSemester === 's1' ? (created >= mid && created <= half) : (created > half && created <= end);
                  };
                  let rows = savedSchedules.filter(s =>
                    (filterClassId==='all' || s.class_id === parseInt(filterClassId)) &&
                    withinAcademicYear(s) && withinSemester(s) &&
                    (`${s.class_name} ${s.section_name}`.toLowerCase().includes(searchTerm.toLowerCase()))
                  );
                  rows.sort((a,b)=>{
                    let va, vb;
                    switch (sortKey) {
                      case 'section': va=a.section_name; vb=b.section_name; break;
                      case 'created': va=new Date(a.created_at).getTime(); vb=new Date(b.created_at).getTime(); break;
                      case 'updated': va=new Date(a.updated_at).getTime(); vb=new Date(b.updated_at).getTime(); break;
                      default: va=a.class_name; vb=b.class_name;
                    }
                    if (va<vb) return sortDir==='asc'? -1:1;
                    if (va>vb) return sortDir==='asc'? 1:-1;
                    return 0;
                  });
                  const total = rows.length;
                  const totalPages = Math.max(1, Math.ceil(total/pageSize));
                  const currentPage = Math.min(page, totalPages);
                  const startIdx = (currentPage-1)*pageSize;
                  rows = rows.slice(startIdx, startIdx+pageSize);
                  return rows.map((schedule, index) => (
                  <div 
                    key={`${schedule.key}_${index}`} 
                    className={`nexy-schedule-card ${selectedSchedule?.key === schedule.key ? 'nexy-selected' : ''}`}
                    onClick={() => handleScheduleSelect(schedule)}
                  >
                    <div className="nexy-card-header">
                      <h3 className="nexy-grade-heading">{schedule.class_name}</h3>
                      <span className="nexy-section-badge">{schedule.section_name}</span>
                    </div>
                    <div className="nexy-timestamps-section">
                      <div className="nexy-timestamp-row">
                        <span className="nexy-timestamp-label">Created:</span>
                        <span className="nexy-timestamp-value">{formatDate(schedule.created_at)}</span>
                      </div>
                      <div className="nexy-timestamp-row">
                        <span className="nexy-timestamp-label">Updated:</span>
                        <span className="nexy-timestamp-value">{formatDate(schedule.updated_at)}</span>
                      </div>
                    </div>
                    <button 
                      className="nexy-view-schedule-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleScheduleSelect(schedule);
                      }}
                    >
                      <span className="nexy-view-icon">👁️</span>
                      View Schedule
                    </button>
                  </div>
                  ));
                })()}
              </div>
            )}
            {/* Pagination Controls */}
            {!loadingSchedules && savedSchedules.length > 0 && (() => {
              const total = savedSchedules.filter(s =>
                (filterClassId==='all' || s.class_id === parseInt(filterClassId)) &&
                (filterAcademicYear==='all' || true) &&
                (filterSemester==='all' || true) &&
                (`${s.class_name} ${s.section_name}`.toLowerCase().includes(searchTerm.toLowerCase()))
              ).length;
              const totalPages = Math.max(1, Math.ceil(total/pageSize));
              const currentPage = Math.min(page, totalPages);
              const isPrevDisabled = currentPage === 1;
              const isNextDisabled = currentPage >= totalPages;
              
              return (
                <div className="nexy-pagination">
                  <button 
                    className="nexy-pagination-btn nexy-prev-btn"
                    onClick={()=> setPage(p => Math.max(1, p-1))}
                    disabled={isPrevDisabled}
                  >
                    Prev
                  </button>
                  <div className="nexy-page-indicator">
                    <span className="nexy-page-label">Page</span>
                    <span className="nexy-page-number">{currentPage}</span>
                  </div>
                  <button 
                    className="nexy-pagination-btn nexy-next-btn"
                    onClick={()=> setPage(p => p+1)}
                    disabled={isNextDisabled}
                  >
                    Next
                  </button>
                  <select 
                    value={pageSize} 
                    onChange={(e)=> { setPageSize(parseInt(e.target.value)); setPage(1);} }
                    className="nexy-items-per-page"
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                  </select>
                </div>
              );
            })()}
          </div>

          {/* Schedule Details */}
          {selectedSchedule && (
            <div className="schedule-details-section">
              <div className="section-header">
                <h2>📊 Schedule Details</h2>
                <div className="schedule-info">
                  <span className="class-info">
                    {selectedSchedule.class_name} - Section {selectedSchedule.section_name}
                  </span>
                </div>
              </div>

              {loadingScheduleData ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading schedule data...</p>
                </div>
              ) : Object.keys(schedulesBySemester).length > 0 ? (
                <div className="semester-schedules-container">
                  {Object.keys(schedulesBySemester).map((semester, index) => {
                    const semesterData = schedulesBySemester[semester];
                    const semesterSchedule = semesterData.schedule;
                    
                    // Get working days and periods for this semester's schedule
                    const getWorkingDaysForSemester = () => {
                      const days = new Set();
                      Object.keys(semesterSchedule).forEach(key => {
                        const [day] = key.split('_');
                        days.add(day);
                      });
                      const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                      return dayOrder.filter(day => days.has(day));
                    };
                    
                    const getPeriodsForSemester = () => {
                      const periods = new Set();
                      Object.keys(semesterSchedule).forEach(key => {
                        const [day, period] = key.split('_');
                        periods.add(parseInt(period));
                      });
                      return Array.from(periods).sort((a, b) => a - b);
                    };
                    
                    const workingDays = getWorkingDaysForSemester();
                    const periods = getPeriodsForSemester();
                    
                    return (
                      <div key={semester} className={`semester-schedule-section ${index === 0 ? 'first-semester' : ''}`}>
                        <div className="semester-header">
                          <h3 className="semester-title">
                            📚 {semesterData.semester_name || semester}
                            {index === 0 && <span className="primary-badge">Primary</span>}
                          </h3>
                        </div>
                        <div className="schedule-table-container">
                          <table className="schedule-table">
                            <thead>
                              <tr className="schedule-table-header">
                                <th className="period-header">Period</th>
                                {workingDays.map(day => (
                                  <th key={day} className="day-header">
                                    {getDayName(day)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {periods.map(period => {
                                const timing = schoolTimings ? calculatePeriodTimings(period) : null;
                                
                                return (
                                  <React.Fragment key={`period_${period}`}>
                                    <tr className="schedule-table-row">
                                      <td className="period-cell">
                                        <div className="period-label">Period {period}</div>
                                        {timing ? (
                                          <div className="period-timing">
                                            <div className="timing-text">{timing.start} - {timing.end}</div>
                                          </div>
                                        ) : (
                                          <div className="period-timing" style={{color: '#dc3545', fontSize: '0.7rem', backgroundColor: '#f8d7da'}}>
                                            No school timings set
                                          </div>
                                        )}
                                      </td>
                                      {workingDays.map(day => {
                                        const key = `${day}_${period}`;
                                        const scheduleItem = semesterSchedule[key] || {};
                                        
                                        return (
                                          <td key={day} className="schedule-cell">
                                            {scheduleItem.subject_name ? (
                                              <div className="schedule-assignment-display">
                                                <div className="subject-display">
                                                  <strong>{scheduleItem.subject_name}</strong>
                                                </div>
                                                {scheduleItem.teacher_name && (
                                                  <div className="teacher-display">
                                                    <span className="teacher-name">{scheduleItem.teacher_name}</span>
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <div className="empty-slot">
                                                <span className="empty-text">Free Period</span>
                                              </div>
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                    {timing && timing.breakAfter && (
                                      <tr key={`break_after_${period}`} className="break-table-row">
                                        <td className="break-period-cell">
                                          <div className="break-label">☕ Break</div>
                                          <div className="break-timing">
                                            {formatTime(timing.breakAfter.startTime)} - {formatTime(timing.breakAfter.endTime)}
                                          </div>
                                        </td>
                                        {workingDays.map(day => (
                                          <td key={day} className="break-cell">
                                            <div className="break-display">
                                              <span className="break-text">Break Time</span>
                                            </div>
                                          </td>
                                        ))}
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : scheduleData && scheduleData.schedule ? (
                <div className="schedule-table-container">
                  <table className="schedule-table">
                    <thead>
                      <tr className="schedule-table-header">
                        <th className="period-header">Period</th>
                        {getWorkingDays().map(day => (
                          <th key={day} className="day-header">
                            {getDayName(day)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const periods = getPeriods();
                        const rows = [];
                        
                        periods.forEach(period => {
                          const timing = schoolTimings ? calculatePeriodTimings(period) : null;
                          
                          rows.push(
                            <tr key={`period_${period}`} className="schedule-table-row">
                              <td className="period-cell">
                                <div className="period-label">Period {period}</div>
                                {timing ? (
                                  <div className="period-timing">
                                    <div className="timing-text">{timing.start} - {timing.end}</div>
                                  </div>
                                ) : (
                                  <div className="period-timing" style={{color: '#dc3545', fontSize: '0.7rem', backgroundColor: '#f8d7da'}}>
                                    No school timings set
                                  </div>
                                )}
                              </td>
                              {getWorkingDays().map(day => {
                                const key = `${day}_${period}`;
                                const scheduleItem = scheduleData.schedule[key] || {};
                                
                                return (
                                  <td key={day} className="schedule-cell">
                                    {scheduleItem.subject_name ? (
                                      <div className="schedule-assignment-display">
                                        <div className="subject-display">
                                          <strong>{scheduleItem.subject_name}</strong>
                                        </div>
                                        {scheduleItem.teacher_name && (
                                          <div className="teacher-display">
                                            <span className="teacher-name">{scheduleItem.teacher_name}</span>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="empty-slot">
                                        <span className="empty-text">Free Period</span>
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                          
                          if (timing && timing.breakAfter) {
                            rows.push(
                              <tr key={`break_after_${period}`} className="break-table-row">
                                <td className="break-period-cell">
                                  <div className="break-label">☕ Break</div>
                                  <div className="break-timing">
                                    {formatTime(timing.breakAfter.startTime)} - {formatTime(timing.breakAfter.endTime)}
                                  </div>
                                </td>
                                {getWorkingDays().map(day => (
                                  <td key={day} className="break-cell">
                                    <div className="break-display">
                                      <span className="break-text">Break Time</span>
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            );
                          }
                        });
                        
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-schedule-data">
                  <div className="no-data-icon">📋</div>
                  <h3>No Schedule Data</h3>
                  <p>This schedule appears to be empty or corrupted.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScheduleView;
